/**
 * @fileoverview Configurações → Departamentos: os grupos de contas da empresa
 * e quem vê os números de cada um no Dashboard (quem gerencia:
 * departments.manage; na regra de hoje, o admin).
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Chip, IconButton, Tooltip, Typography, makeStyles } from "@material-ui/core";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "react-toastify";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import usePermissionStore from "../../stores/usePermissionStore";
import { i18n } from "../../translate/i18n";
import ConfirmationModal from "../ConfirmationModal";
import { LoadError, LoadingState } from "../Settings/LoadState";
import DepartmentModal from "./DepartmentModal";

// Chips por linha antes do "+N".
const SHOWN = 3;

const useStyles = makeStyles((theme) => {
  const dark = theme.palette.type === "dark";
  const line = dark ? "rgba(255, 255, 255, 0.06)" : "rgba(15, 23, 42, 0.08)";
  const muted = dark ? "#94a3b8" : "#64748b";
  // A cor do texto nos dois temas (o Typography sem cor fica preto no escuro).
  const text = dark ? "#f8fafc" : "#0f172a";
  return {
    header: {
      display: "flex",
      justifyContent: "flex-end",
      marginBottom: theme.spacing(2),
    },
    addButton: {
      height: 36,
      borderRadius: 10,
      textTransform: "none",
      fontWeight: 500,
      fontSize: "0.875rem",
      padding: "0 14px",
    },
    table: {
      border: `1px solid ${line}`,
      borderRadius: 12,
      overflow: "hidden",
    },
    row: {
      display: "grid",
      gridTemplateColumns: "minmax(140px, 1fr) minmax(0, 2fr) minmax(0, 2fr) 80px",
      gap: theme.spacing(2),
      alignItems: "center",
      padding: theme.spacing(1.5, 2),
      borderTop: `1px solid ${line}`,
      "&:first-child": { borderTop: "none" },
      [theme.breakpoints.down("sm")]: {
        gridTemplateColumns: "1fr auto",
        "& > :nth-child(2), & > :nth-child(3)": { gridColumn: "1 / -1" },
      },
    },
    headRow: {
      [theme.breakpoints.down("sm")]: { display: "none" },
    },
    headText: {
      fontSize: "0.72rem",
      fontWeight: 600,
      textTransform: "uppercase",
      letterSpacing: "0.06em",
      color: muted,
    },
    name: {
      fontWeight: 600,
      fontSize: "0.92rem",
      color: text,
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
    chips: {
      display: "flex",
      flexWrap: "wrap",
      gap: 6,
      minWidth: 0,
    },
    chip: {
      maxWidth: 180,
    },
    nobody: {
      fontSize: "0.85rem",
      color: muted,
    },
    actions: {
      display: "flex",
      justifyContent: "flex-end",
      gap: 4,
      [theme.breakpoints.down("sm")]: { gridRow: 1, gridColumn: 2 },
    },
    iconButton: {
      padding: 6,
      borderRadius: 8,
      color: muted,
    },
    empty: {
      textAlign: "center",
      padding: theme.spacing(5, 2),
      color: muted,
      fontSize: "0.92rem",
    },
  };
});

const DepartmentsManager = () => {
  const classes = useStyles();
  const [departments, setDepartments] = useState([]);
  const [options, setOptions] = useState(null);
  const [loadState, setLoadState] = useState("loading");
  const [editing, setEditing] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const [list, choices] = await Promise.all([api.get("/departments"), api.get("/departments/options")]);
      setDepartments(list.data || []);
      setOptions(choices.data || null);
      setLoadState("ready");
    } catch (err) {
      toastError(err);
      setLoadState("error");
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const departmentNames = useMemo(() => new Map(departments.map((department) => [department.id, department.name])), [departments]);

  const openModal = (department) => {
    setEditing(department);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditing(null);
  };

  const save = async (data) => {
    setSaving(true);
    try {
      if (editing?.id) {
        await api.put(`/departments/${editing.id}`, data);
        toast.success(i18n.t("settings.departments.toasts.updated"));
      } else {
        await api.post("/departments", data);
        toast.success(i18n.t("settings.departments.toasts.created"));
      }
      closeModal();
      // O filtro "Números de" sai do acesso: o primeiro departamento da conta o mostra.
      usePermissionStore.getState().load();
      await load();
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!deleting?.id) return;
    try {
      await api.delete(`/departments/${deleting.id}`);
      toast.success(i18n.t("settings.departments.toasts.deleted"));
      usePermissionStore.getState().load();
      await load();
    } catch (err) {
      toastError(err);
    } finally {
      setDeleting(null);
    }
  };

  const chipList = (items) => (
    <div className={classes.chips}>
      {items.slice(0, SHOWN).map((item) => (
        <Chip key={item.id} variant="outlined" size="small" label={item.name} className={classes.chip} />
      ))}
      {items.length > SHOWN && (
        <Tooltip title={items.slice(SHOWN).map((item) => item.name).join(", ")}>
          <Chip variant="outlined" size="small" label={`+${items.length - SHOWN}`} />
        </Tooltip>
      )}
    </div>
  );

  if (loadState === "loading") return <LoadingState />;
  if (loadState === "error") {
    return (
      <LoadError
        onRetry={() => {
          setLoadState("loading");
          load();
        }}
      />
    );
  }

  return (
    <div>
      <ConfirmationModal
        title={i18n.t("settings.departments.confirmationModal.deleteTitle")}
        open={!!deleting}
        onClose={() => setDeleting(null)}
        onConfirm={remove}
      >
        {i18n.t("settings.departments.confirmationModal.deleteMessage")}
      </ConfirmationModal>

      <DepartmentModal
        open={modalOpen}
        onClose={closeModal}
        onSubmit={save}
        department={editing}
        options={options}
        departmentNames={departmentNames}
        loading={saving}
      />

      <div className={classes.header}>
        <Button variant="contained" color="primary" startIcon={<Plus size={16} />} onClick={() => openModal(null)} className={classes.addButton}>
          {i18n.t("settings.departments.buttons.add")}
        </Button>
      </div>

      {departments.length === 0 ? (
        <Typography className={classes.empty}>{i18n.t("settings.departments.emptyState")}</Typography>
      ) : (
        <div className={classes.table}>
          <div className={`${classes.row} ${classes.headRow}`}>
            <Typography className={classes.headText}>{i18n.t("settings.departments.columns.name")}</Typography>
            <Typography className={classes.headText}>{i18n.t("settings.departments.columns.companies")}</Typography>
            <Typography className={classes.headText}>{i18n.t("settings.departments.columns.viewers")}</Typography>
            <span />
          </div>
          {departments.map((department) => (
            <div key={department.id} className={classes.row} data-department={department.id}>
              <Typography className={classes.name} title={department.name}>
                {department.name}
              </Typography>
              {chipList(department.companies)}
              {department.viewers.length ? (
                chipList(department.viewers)
              ) : (
                <Typography className={classes.nobody}>{i18n.t("settings.departments.nobody")}</Typography>
              )}
              <div className={classes.actions}>
                <Tooltip title={i18n.t("settings.departments.buttons.edit")}>
                  <IconButton size="small" className={classes.iconButton} onClick={() => openModal(department)}>
                    <Pencil size={16} strokeWidth={1.75} />
                  </IconButton>
                </Tooltip>
                <Tooltip title={i18n.t("settings.departments.buttons.delete")}>
                  <IconButton size="small" className={classes.iconButton} onClick={() => setDeleting(department)}>
                    <Trash2 size={16} strokeWidth={1.75} />
                  </IconButton>
                </Tooltip>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default DepartmentsManager;
